"use client";

import { useMemo, useState, type ReactNode } from "react";
import { Library } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileType, fileAcceptMapping } from "@/components/utils/uploadFile";
import type { UploadLimits } from "@/components/utils/uploadBatch";
import { useLocale } from "@/hooks/useLocale";
import { t } from "@/lib/localization";
import { cn } from "@/lib/utils";
import type { LomdaV3AssetType } from "@/lib/utils/lomdaV3/types";
import type { LomdaLibraryItem } from "../../../lib/mediaAssets";
import { useMediaLibrary } from "../../../hooks/useMediaLibrary";
import {
    PickGuardContext,
    usePickGuard,
    type PickGuard,
} from "../../../hooks/usePickGuard";
import { PopoverTitle, popoverShell } from "../../toolbar/blockAppearance";
import AssetThumb, { assetName } from "./assetThumb";
import { LOMDA_DROPZONE_FRAME_CLASS } from "./dropzone";

/** `canvas`: an empty block on the page. `panel`: inside a popover.
 *  `compact`: a slot too small for a list, so the list opens in a popover. */
export type MediaSourceLayout = "canvas" | "panel" | "compact";

const EMPTY_KEY: Record<LomdaV3AssetType, string> = {
    image: "lomda-v3-editor.assets.no-images",
    video: "lomda-v3-editor.assets.no-videos",
    audio: "lomda-v3-editor.assets.no-audio",
    file: "lomda-v3-editor.assets.no-files",
};

const PANEL_LIST_CLASS = "max-h-60 overflow-y-auto p-0.5";
const SELECTED_CLASS = "ring-2 ring-[hsl(var(--platform-primary))]";
const PICK_CLASS =
    "transition hover:ring-2 hover:ring-[hsl(var(--platform-primary))]";
const BLOCKED_CLASS = "cursor-not-allowed opacity-40";

const fileTypesOfExt = (ext: string) =>
    (Object.keys(fileAcceptMapping) as FileType[]).filter((type) =>
        Object.values(fileAcceptMapping[type]).flat().includes(ext)
    );

/** Why the slot's upload would reject `item`, or null. An unknown size or
 *  extension passes, since the upload may have accepted it by MIME type. */
function limitReason(item: LomdaLibraryItem, limits?: UploadLimits) {
    if (!limits) return null;
    const ext = item.url.split(/[?#]/)[0].match(/\.[a-z0-9]+$/i)?.[0];
    const types = ext ? fileTypesOfExt(ext.toLowerCase()) : [];
    if (types.length && !types.some((x) => limits.allowedFileTypes.includes(x)))
        return t("lomda-v3-editor.assets.slot-wrong-type");
    if (item.size && item.size / 1024 / 1024 > limits.maxFileSize)
        return t("lomda-v3-editor.assets.slot-too-large", undefined, {
            max: limits.maxFileSize,
        });
    return null;
}

interface PickProps {
    kind: LomdaV3AssetType;
    onPick: (item: LomdaLibraryItem) => void;
    selectedUrl?: string;
    limits?: UploadLimits;
}

function PickButton({
    item,
    limits,
    selectedUrl,
    onPick,
    className,
    children,
}: Omit<PickProps, "kind"> & {
    item: LomdaLibraryItem;
    className: string;
    children: ReactNode;
}) {
    const blocked = limitReason(item, limits);
    return (
        <button
            type="button"
            title={blocked ?? assetName(item)}
            aria-disabled={blocked ? true : undefined}
            onClick={() => !blocked && onPick(item)}
            className={cn(
                className,
                blocked ? BLOCKED_CLASS : PICK_CLASS,
                item.url === selectedUrl && SELECTED_CLASS
            )}
        >
            {children}
        </button>
    );
}

function LibraryList({
    kind,
    className,
    ...pick
}: PickProps & { className?: string }) {
    const library = useMediaLibrary();
    // Storage videos have no URL; the video block's "From storage" picks them.
    const items = useMemo(
        () =>
            library.filter(
                (item) => item.type === kind && !item.isIcon && !!item.url
            ),
        [library, kind]
    );

    if (items.length === 0)
        return (
            <p
                className={cn(
                    "py-6 text-center text-sm text-muted-foreground",
                    className
                )}
            >
                {t(EMPTY_KEY[kind])}
            </p>
        );

    if (kind === "audio" || kind === "file")
        return (
            <div className={cn("flex flex-col gap-1.5", className)}>
                {items.map((item) => (
                    <PickButton
                        key={item.key}
                        item={item}
                        {...pick}
                        className="flex min-w-0 items-center gap-2 rounded-md border px-2 py-1.5 text-start text-sm"
                    >
                        <span className="flex size-8 shrink-0 items-center justify-center rounded bg-muted">
                            <AssetThumb item={item} name={assetName(item)} />
                        </span>
                        <span className="truncate">{assetName(item)}</span>
                    </PickButton>
                ))}
            </div>
        );

    return (
        <div
            className={cn(
                "grid content-start gap-2",
                kind === "video"
                    ? "grid-cols-[repeat(auto-fill,minmax(7rem,1fr))]"
                    : "grid-cols-[repeat(auto-fill,minmax(5rem,1fr))]",
                className
            )}
        >
            {items.map((item) => (
                <PickButton
                    key={item.key}
                    item={item}
                    {...pick}
                    className="flex min-w-0 flex-col gap-1 overflow-hidden rounded-md text-start"
                >
                    <span
                        className={cn(
                            "relative flex items-center justify-center overflow-hidden rounded-md border bg-muted",
                            kind === "video" ? "aspect-video" : "aspect-square"
                        )}
                    >
                        <AssetThumb item={item} name={assetName(item)} />
                    </span>
                    {kind === "video" && (
                        <span className="truncate px-0.5 text-xs">
                            {assetName(item)}
                        </span>
                    )}
                </PickButton>
            ))}
        </div>
    );
}

function CompactSource({
    upload,
    onPick,
    ...props
}: PickProps & { upload: ReactNode }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="lomda-v3-chrome flex flex-col gap-1.5">
            {upload}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        className="flex h-7 w-full items-center justify-center gap-1 rounded-md border border-dashed border-[hsl(var(--lomda-border-dashed))] px-2 text-xs text-[hsl(var(--lomda-muted-text))] transition hover:border-[hsl(var(--lomda-border-active))] hover:text-[hsl(var(--lomda-text))] data-[state=open]:border-[hsl(var(--lomda-border-active))] data-[state=open]:bg-[hsl(var(--lomda-muted))] data-[state=open]:text-[hsl(var(--lomda-text))]"
                    >
                        <Library className="size-3.5 shrink-0" />
                        <span className="truncate">
                            {t("lomda-v3-editor.media.tab-library")}
                        </span>
                    </button>
                </PopoverTrigger>
                <PopoverContent align="start" className={cn("w-72", popoverShell)}>
                    <div className="flex flex-col gap-4">
                        <PopoverTitle>
                            {t("lomda-v3-editor.media.tab-library")}
                        </PopoverTitle>
                        <LibraryList
                            {...props}
                            className={PANEL_LIST_CLASS}
                            onPick={(item) => {
                                onPick(item);
                                setOpen(false);
                            }}
                        />
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}

/** "Upload" / "Reuse media" for any media slot. The library tab lists only
 *  files of the slot's `kind`; picking one sets it without uploading again,
 *  and a pick wins over an upload still running in `upload`. */
export function MediaSourceTabs({
    guard: sharedGuard,
    ...rest
}: PickProps & {
    upload: ReactNode;
    layout?: MediaSourceLayout;
    guard?: PickGuard;
}) {
    const ownGuard = usePickGuard();
    const guard = sharedGuard ?? ownGuard;
    return (
        <PickGuardContext.Provider value={guard}>
            <SourceTabs
                {...rest}
                onPick={(item) => {
                    guard.markPick();
                    rest.onPick(item);
                }}
            />
        </PickGuardContext.Provider>
    );
}

function SourceTabs({
    upload,
    layout = "panel",
    ...props
}: PickProps & { upload: ReactNode; layout?: MediaSourceLayout }) {
    const { dir } = useLocale();
    const [tab, setTab] = useState("upload");

    if (layout === "compact")
        return <CompactSource upload={upload} {...props} />;

    const canvas = layout === "canvas";
    return (
        <Tabs
            value={tab}
            onValueChange={setTab}
            dir={dir}
            className="lomda-v3-chrome flex flex-col gap-3"
        >
            <TabsList
                className={canvas ? "self-center" : "grid w-full grid-cols-2"}
            >
                <TabsTrigger value="upload">
                    {t("lomda-v3-editor.media.tab-upload")}
                </TabsTrigger>
                <TabsTrigger value="library">
                    {t("lomda-v3-editor.media.tab-library")}
                </TabsTrigger>
            </TabsList>
            {/* Kept mounted so switching tabs mid-upload keeps its progress. */}
            <TabsContent
                value="upload"
                forceMount
                className="mt-0 data-[state=inactive]:hidden"
            >
                {upload}
            </TabsContent>
            <TabsContent value="library" className="mt-0">
                <LibraryList
                    {...props}
                    className={
                        canvas
                            ? cn(
                                  LOMDA_DROPZONE_FRAME_CLASS,
                                  "max-h-[360px] min-h-[240px] overflow-y-auto rounded-[20px] p-4"
                              )
                            : PANEL_LIST_CLASS
                    }
                />
            </TabsContent>
        </Tabs>
    );
}
